<template>
  <div class="alert-email-configure info-item">
    <span class="key">告警邮箱：</span>
    <span class="value">已配置 {{ recipientCount }}/{{ maxRecipientCount }}</span>
    <span class="edit" @click="openDialog">
      <i class="iconfont iconbianji"></i>配置
    </span>

    <el-dialog
      :visible.sync="visible"
      width="620px"
      :show-close="false"
      :close-on-click-modal="false"
      @closed="resetInput"
    >
      <div class="dialog-content">
        <i class="iconfont iconguanbi close" @click="visible=false"></i>
        <div class="dialog-header">告警邮箱配置</div>
        <div class="content-wrapper" v-loading="loading">
          <p class="description">数据产品的配额及到期告警将分别发送至以下邮箱。</p>

          <div class="email-section">
            <div class="section-title">绑定账号邮箱</div>
            <div class="email-row account-email-row">
              <span class="email-text">{{ accountEmail }}</span>
              <span class="default-label">默认接收</span>
            </div>
          </div>

          <div class="email-section additional-section">
            <div class="section-title">
              <span>附加告警邮箱</span>
              <span class="count">{{ additionalEmails.length }}/{{ additionalLimit }}</span>
            </div>
            <div v-if="additionalEmails.length" class="email-list">
              <div v-for="email in additionalEmails" :key="email" class="email-row">
                <span class="email-text" :title="email">{{ email }}</span>
                <el-button
                  type="text"
                  icon="el-icon-delete"
                  title="删除"
                  :disabled="deletingEmail === email"
                  :loading="deletingEmail === email"
                  @click="confirmDelete(email)"
                ></el-button>
              </div>
            </div>
            <div v-else class="empty-list">暂未添加告警邮箱</div>
          </div>

          <div class="batch-section">
            <div class="section-title">批量添加</div>
            <el-input
              v-model="batchInput"
              type="textarea"
              :rows="4"
              :disabled="isFull || submitting"
              placeholder="输入邮箱，多个邮箱可用换行、逗号或分号分隔"
              @focus="inputErrors=[]"
            ></el-input>
            <div class="input-meta">
              <span>还可添加 {{ remainingCount }} 个</span>
              <span v-if="isFull" class="limit-tip">已达到收件人数上限</span>
            </div>
            <div v-if="inputErrors.length" class="error-list">
              <p v-for="(item, index) in inputErrors" :key="`${item.email}-${index}`">
                {{ item.email || `第 ${index + 1} 项` }}：{{ item.message }}
              </p>
            </div>
          </div>

          <div class="btn-group">
            <div class="btn cancel" @click="visible=false">关闭</div>
            <div
              class="btn submit"
              :class="{ disabled: isFull || submitting || !batchInput.trim() }"
              @click="submitBatch"
            >
              {{ submitting ? '添加中...' : '添加邮箱' }}
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { addAlertEmails, deleteAlertEmail, getAlertEmailConfig } from '@/services/interface'

const EMAIL_REGEXP = /^([A-Za-z0-9_+-.])+@([A-Za-z0-9\-.])+\.([A-Za-z]{2,22})$/

export default {
  props: {
    userEmail: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      visible: false,
      loading: false,
      submitting: false,
      deletingEmail: '',
      accountEmail: this.userEmail,
      additionalEmails: [],
      recipientCount: 1,
      maxRecipientCount: 100,
      batchInput: '',
      inputErrors: []
    }
  },
  computed: {
    additionalLimit () {
      return Math.max(this.maxRecipientCount - 1, 0)
    },
    remainingCount () {
      return Math.max(this.maxRecipientCount - this.recipientCount, 0)
    },
    isFull () {
      return this.remainingCount === 0
    }
  },
  watch: {
    userEmail (value) {
      if (value) {
        this.accountEmail = value
      }
    }
  },
  created () {
    this.loadConfig(false)
  },
  methods: {
    applyConfig (data) {
      this.accountEmail = data.account_email || this.userEmail
      this.additionalEmails = data.additional_emails || []
      this.recipientCount = data.recipient_count || 1
      this.maxRecipientCount = data.max_recipient_count || 100
    },
    async loadConfig (showError = true) {
      this.loading = true
      try {
        const res = await getAlertEmailConfig()
        if (res.status === 200) {
          this.applyConfig(res.data)
        }
      } catch (err) {
        if (showError) {
          this.$message.error('告警邮箱配置加载失败，请稍后重试')
        }
      } finally {
        this.loading = false
      }
    },
    openDialog () {
      this.visible = true
      this.loadConfig()
    },
    resetInput () {
      this.batchInput = ''
      this.inputErrors = []
    },
    parseBatch () {
      return this.batchInput
        .split(/[\n,，;；]+/)
        .map(email => email.trim())
        .filter(Boolean)
    },
    validateBatch (emails) {
      const errors = []
      const seen = new Set()
      const existing = new Set(this.additionalEmails.map(email => email.toLowerCase()))
      const accountEmail = this.accountEmail.toLowerCase()

      if (!emails.length) {
        return [{ email: '', message: '请输入至少一个邮箱' }]
      }

      emails.forEach((email, index) => {
        const normalized = email.toLowerCase()
        if (email.length > 64 || !EMAIL_REGEXP.test(email)) {
          errors.push({ email, message: '邮箱格式错误' })
        } else if (normalized === accountEmail) {
          errors.push({ email, message: '不能与绑定账号邮箱相同' })
        } else if (existing.has(normalized)) {
          errors.push({ email, message: '该邮箱已添加' })
        } else if (seen.has(normalized)) {
          errors.push({ email, message: '本批次邮箱重复' })
        } else if (index >= this.remainingCount) {
          errors.push({ email, message: '超过告警收件人数上限' })
        }
        seen.add(normalized)
      })
      return errors
    },
    applyServerErrors (err, emails) {
      const fields = err && err.data && err.data.fields
      const emailFields = fields && fields.emails
      if (!emailFields || Array.isArray(emailFields)) {
        this.inputErrors = [{ email: '', message: '添加失败，请检查邮箱后重试' }]
        return
      }
      this.inputErrors = Object.keys(emailFields).map(index => ({
        email: emails[Number(index)] || '',
        message: Array.isArray(emailFields[index]) ? emailFields[index][0] : emailFields[index]
      }))
    },
    async submitBatch () {
      if (this.isFull || this.submitting || !this.batchInput.trim()) {
        return
      }
      const emails = this.parseBatch()
      this.inputErrors = this.validateBatch(emails)
      if (this.inputErrors.length) {
        return
      }

      this.submitting = true
      try {
        const res = await addAlertEmails({ emails })
        if (res.status === 201 || res.status === 200) {
          this.applyConfig(res.data)
          this.batchInput = ''
          this.inputErrors = []
          this.$message.success('告警邮箱添加成功')
        }
      } catch (err) {
        this.applyServerErrors(err, emails)
      } finally {
        this.submitting = false
      }
    },
    confirmDelete (email) {
      if (this.deletingEmail) {
        return
      }
      this.$confirm(`确定删除告警邮箱 ${email} 吗？`, '删除告警邮箱', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => this.handleDelete(email)).catch(() => {})
    },
    async handleDelete (email) {
      this.deletingEmail = email
      try {
        const res = await deleteAlertEmail({ email })
        if (res.status === 200) {
          this.applyConfig(res.data)
          this.$message.success('告警邮箱已删除')
        }
      } catch (err) {
        this.$message.error('删除失败，请稍后重试')
      } finally {
        this.deletingEmail = ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.alert-email-configure {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  color: rgba(29, 36, 60, 1);
  line-height: 20px;
  .key {
    width: 70px;
    margin-right: 8px;
  }
  .value {
    width: 200px;
    margin-right: 10px;
  }
  .edit {
    color: #003d92;
    cursor: pointer;
  }
  /deep/.el-dialog__header {
    padding: 0;
  }
  /deep/.el-dialog__body {
    padding: 0;
  }
  /deep/.el-dialog {
    max-width: calc(100vw - 24px);
  }
}

.dialog-content {
  position: relative;
  .close {
    position: absolute;
    top: 18px;
    right: 20px;
    z-index: 1;
    color: #fff;
    font-size: 14px;
    cursor: pointer;
  }
  .dialog-header {
    height: 50px;
    background: #003d92;
    color: #fff;
    font-size: 16px;
    line-height: 50px;
    text-align: center;
  }
  .content-wrapper {
    min-height: 430px;
    padding: 28px 44px 30px;
    box-sizing: border-box;
  }
}

.description {
  margin-bottom: 22px;
  color: #6f799c;
  font-size: 13px;
  line-height: 20px;
}

.email-section,
.batch-section {
  margin-bottom: 22px;
  .section-title {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    color: #404964;
    font-size: 14px;
    font-weight: bold;
    .count {
      color: #6f799c;
      font-size: 12px;
      font-weight: normal;
    }
  }
}

.email-row {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 0 12px;
  border-bottom: 1px solid #eef0f5;
  box-sizing: border-box;
  .email-text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    color: #1d243c;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /deep/.el-button {
    width: 32px;
    min-width: 32px;
    padding: 8px;
    color: #6f799c;
  }
}

.account-email-row {
  border: 1px solid #e6e7f0;
  background: #f8f9fc;
  .default-label {
    margin-left: 12px;
    color: #1376ff;
    font-size: 12px;
  }
}

.email-list {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #e6e7f0;
  border-bottom: 0;
}

.empty-list {
  height: 52px;
  border: 1px dashed #dcdfe6;
  color: #9a9ead;
  font-size: 13px;
  line-height: 52px;
  text-align: center;
}

.input-meta {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  color: #9a9ead;
  font-size: 12px;
  .limit-tip {
    color: #ef3b45;
  }
}

.error-list {
  max-height: 72px;
  margin-top: 8px;
  overflow-y: auto;
  color: #ef3b45;
  font-size: 12px;
  line-height: 18px;
  word-break: break-all;
}

.btn-group {
  display: flex;
  justify-content: flex-end;
  margin-top: 26px;
  .btn {
    width: 112px;
    height: 40px;
    border-radius: 4px;
    font-size: 14px;
    line-height: 40px;
    text-align: center;
    cursor: pointer;
    box-sizing: border-box;
  }
  .cancel {
    margin-right: 16px;
    border: 1px solid #dcdfe6;
    color: #6f799c;
  }
  .submit {
    background: #003d92;
    color: #fff;
    &.disabled {
      background: #d6d7dc;
      cursor: not-allowed;
    }
  }
}

@media (max-width: 768px) {
  .alert-email-configure {
    /deep/.el-dialog {
      margin: 24px auto 0 !important;
    }
  }
  .dialog-content {
    .content-wrapper {
      min-height: 0;
      max-height: calc(100vh - 98px);
      padding: 24px 20px 20px;
      overflow-y: auto;
    }
  }
  .btn-group {
    .btn {
      flex: 1;
      width: auto;
    }
  }
}
</style>
